// ScrollToTopFab.tsx
import React, { useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import styled from 'styled-components'
import './_scrollToTop.scss'

type Props = {
  scrollContainerRef?: React.RefObject<HTMLElement>
  showAfterRows?: number
  estimatedRowPx?: number

  keepVisibleAfterStopMs?: number
  hideNearTopPx?: number

  bottomPx?: number
  rightPx?: number
}

export default function ScrollToTopFab({
  scrollContainerRef,
  showAfterRows = 6,
  estimatedRowPx = 302,

  keepVisibleAfterStopMs = 1200,
  hideNearTopPx = 40,

  bottomPx = 80,
  rightPx = 16,
}: Props) {
  const [visible, _setVisible] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  const visibleRef = useRef(false)
  const setVisible = (next: boolean) => {
    if (visibleRef.current === next) return
    visibleRef.current = next
    _setVisible(next)

    if (next) {
      setShouldRender(true)
    } else {
      // keep mounted briefly so CSS transition can fade out
      window.setTimeout(() => {
        if (!visibleRef.current) setShouldRender(false)
      }, 220)
    }
  }

  const rafRef = useRef<number | null>(null)
  const idleTimerRef = useRef<number | null>(null)

  const lastTopRef = useRef(0)
  const armedRef = useRef(false)

  const getEl = () => {
    return scrollContainerRef?.current ?? (document.querySelector('.home'))
  }

  useEffect(() => {
    const el = getEl()
    if (!el) return

    const showAtPx = estimatedRowPx * showAfterRows

    lastTopRef.current = el.scrollTop
    armedRef.current = el.scrollTop >= showAtPx

    const clearIdle = () => {
      if (idleTimerRef.current != null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }

    const scheduleIdleHide = () => {
      if (!keepVisibleAfterStopMs) return
      clearIdle()

      idleTimerRef.current = window.setTimeout(() => {
        setVisible(false)
      }, keepVisibleAfterStopMs)
    }

    const update = () => {
      rafRef.current = null

      const st = el.scrollTop
      const prev = lastTopRef.current
      const diff = st - prev // + = down, - = up

      if (st <= hideNearTopPx) {
        armedRef.current = false
        clearIdle()
        setVisible(false)
        lastTopRef.current = st
        return
      }

      if (!armedRef.current) {
        if (st >= showAtPx) armedRef.current = true
        clearIdle()
        setVisible(false)
        lastTopRef.current = st
        return
      }

      if (diff < 0) {
        // scrolling up
        setVisible(true)
        scheduleIdleHide()
      } else if (diff > 0) {
        // scrolling down
        clearIdle()
        setVisible(false)
      }

      lastTopRef.current = st
    }

    const onScroll = () => {
      if (rafRef.current != null) return
      rafRef.current = requestAnimationFrame(update)
    }

    setVisible(false)
    el.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      el.removeEventListener('scroll', onScroll)
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      clearIdle()
    }
  }, [scrollContainerRef, showAfterRows, estimatedRowPx, keepVisibleAfterStopMs, hideNearTopPx])

  const scrollToTop = () => {
    const el = getEl()
    if (!el) return

    try {
      el.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      el.scrollTop = 0
    }
  }

  if (!shouldRender) return null

  return createPortal(
    <Fab
      type="button"
      onClick={scrollToTop}
      aria-label="Scroll to top"
      $bottomPx={bottomPx}
      $rightPx={rightPx}
      $visible={visible}
    >
      <Icon className="scrollToTop" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <polyline className="chev chev-1" points="20 28 32 16 44 28" />
        <polyline className="chev chev-2" points="20 41 32 29 44 41" />
        <polyline className="chev chev-3" points="20 54 32 42 44 54" />
      </Icon>
    </Fab>,
    document.body
  )
}

const Fab = styled.button<{ $bottomPx: number; $rightPx: number; $visible: boolean }>`
  position: fixed;
  bottom: ${({ $bottomPx }) => `${$bottomPx}px`};
  right: ${({ $rightPx }) => `${$rightPx}px`};

  width: 50px;
  height: 50px;
  border-radius: 999px;

  border: 0;
  background: #fff;
  cursor: pointer;
  z-index: 9999;

  box-shadow:
    0 10px 30px rgba(15, 23, 42, 0.12),
    0 2px 10px rgba(15, 23, 42, 0.08);

  display: flex;
  align-items: center;
  justify-content: center;

  -webkit-tap-highlight-color: transparent;

  /* fade transition */
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0px)' : 'translateY(6px)')};
  transition: opacity 200ms ease, transform 200ms ease;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  &:active {
    transform: translateY(1px);
  }

  @media (min-width: 768px) {
    display: none;
  }
`

const Icon = styled.svg`
  width: 30px;
  height: 30px;
`
